import type { WCardSuiteSearchView, FavoriteCardView } from '@zola/svc-web-api-ts-client';
import { checkUserFlag, setUserSetting } from '@zola-helpers/client/dist/es/util/userSettings';
import type { FavoriteCardSuites, FavoriteCardSuiteUUIDs } from 'favorites/types';
import { openModal, hideModal } from 'cards/actions/modalActions';
import { getTypeFromBackendType } from 'cards/util/cardTypes';
import ApiService from '../../util/api';
import type { AppThunk } from '../../reducers';
import * as ActionTypes from './types';
import type {
  AddFavoriteCardSuiteAction,
  RemoveFavoriteCardSuiteAction,
  ReceiveFavoriteCardSuiteUUIDsAction,
  ReceiveFavoriteCardSuitesAction,
} from './types';

/* eslint-disable  @typescript-eslint/no-explicit-any */

function addFavoriteCardSuiteToStore(cardSuiteUUID: string): AddFavoriteCardSuiteAction {
  return {
    type: ActionTypes.ADD_FAVORITE_CARD_SUITE,
    payload: cardSuiteUUID,
  };
}

function removeFavoriteCardSuiteFromStore(cardSuiteUUID: string): RemoveFavoriteCardSuiteAction {
  return {
    type: ActionTypes.REMOVE_FAVORITE_CARD_SUITE,
    payload: cardSuiteUUID,
  };
}

export function receiveFavoriteCardSuiteUUIDs(
  cardSuiteUUIDs: FavoriteCardSuiteUUIDs
): ReceiveFavoriteCardSuiteUUIDsAction {
  return {
    type: ActionTypes.RECEIVE_FAVORITE_CARD_SUITE_UUIDS,
    payload: cardSuiteUUIDs,
  };
}

function receiveFavoriteCardSuites(
  cardSuiteDetails: FavoriteCardSuites
): ReceiveFavoriteCardSuitesAction {
  return {
    type: ActionTypes.RECEIVE_FAVORITE_CARD_SUITES,
    payload: cardSuiteDetails,
  };
}

export function addFavoriteCardSuite(cardSuiteUUID: string): AppThunk<Promise<void>> {
  return (dispatch): Promise<void> => {
    dispatch(addFavoriteCardSuiteToStore(cardSuiteUUID));
    return ApiService.post('/web-api/v1/card-catalog/favorites', { cardSuiteUUID }).catch(() => {
      dispatch(removeFavoriteCardSuiteFromStore(cardSuiteUUID));
    });
  };
}

export function removeFavoriteCardSuite(cardSuiteUUID: string): AppThunk<void> {
  return (dispatch): void => {
    dispatch(removeFavoriteCardSuiteFromStore(cardSuiteUUID));
    ApiService.delete('/web-api/v1/card-catalog/favorites', { cardSuiteUUID }).catch(() => {
      dispatch(addFavoriteCardSuiteToStore(cardSuiteUUID));
    });
  };
}

// Only make a single fetchFavoriteCardSuiteUUIDs API request at a time.
let FETCH_UUIDS_PROMISE: Promise<void> | null = null;

export function fetchFavoriteCardSuiteUUIDs(): AppThunk<void> {
  return (dispatch): void => {
    if (!FETCH_UUIDS_PROMISE) {
      FETCH_UUIDS_PROMISE = ApiService.get('/web-api/v1/card-catalog/favorites')
        .then((json: Array<FavoriteCardView>) => json?.map(card => card.card_suite_uuid) || [])
        .then((favoriteUUIDs: Array<any>) => {
          dispatch(receiveFavoriteCardSuiteUUIDs(favoriteUUIDs));
          FETCH_UUIDS_PROMISE = null;
        })
        .catch(() => {
          FETCH_UUIDS_PROMISE = null;
        });
    }
  };
}

// Only make a single fetchFavoriteCardSuiteUUIDs API request at a time.
let FETCH_SUITES_PROMISE: Promise<void> | null = null;
const noop = () => {
  /* Do nothing */
};

export function fetchFavoriteCardSuites(): AppThunk<Promise<void>> {
  return (dispatch): Promise<void> => {
    if (!FETCH_SUITES_PROMISE) {
      FETCH_SUITES_PROMISE = Promise.all([
        ApiService.post('/web-api/v1/card-catalog/favorites/search/faceted', {
          offset: 0,
          limit: 500,
          active_only: true,
        }),
        ApiService.post('/web-api/v1/card-catalog/favorites/search/faceted', {
          offset: 0,
          limit: 500,
          active_only: true,
          digital_suite: true,
        }),
      ])
        .then((json: Array<WCardSuiteSearchView>) => {
          if (Array.isArray(json) && json[0]?.suites && json[1]?.suites) {
            return json[0]?.suites?.concat(json[1]?.suites) || [];
          }
          return json[0]?.suites || [];
        })
        .then((cardSuites: Array<any>) => {
          const formattedCardSuites = cardSuites.map((suite, i: number) => ({
            ...suite.lead_card,
            suiteUUID: suite.uuid,
            suiteName: suite.name,
            collaborator: suite.collaborator,
            thirdParty: !!suite.third_party,
            singleSampleAvailable: suite.single_sample_available,
            singleSampleSku: suite.single_sample_sku,
            searchIndex: i,
            type: getTypeFromBackendType(suite.lead_card.type),
          }));

          dispatch(receiveFavoriteCardSuites(formattedCardSuites));
          FETCH_SUITES_PROMISE = null;
        })
        .catch(() => {
          FETCH_SUITES_PROMISE = null;
        });
    } else if (!FETCH_SUITES_PROMISE) {
      FETCH_SUITES_PROMISE = ApiService.post('/web-api/v1/card-catalog/favorites/search/faceted', {
        offset: 0,
        limit: 500,
        active_only: true,
      })
        .then((json: WCardSuiteSearchView) => json.suites || [])
        .then((cardSuites: Array<any>) => {
          const formattedCardSuites = cardSuites.map((suite, i: number) => ({
            ...suite.lead_card,
            suiteUUID: suite.uuid,
            suiteName: suite.name,
            collaborator: suite.collaborator,
            thirdParty: !!suite.third_party,
            singleSampleAvailable: suite.single_sample_available,
            singleSampleSku: suite.single_sample_sku,
            searchIndex: i,
            type: getTypeFromBackendType(suite.lead_card.type),
          }));

          dispatch(receiveFavoriteCardSuites(formattedCardSuites));
          FETCH_SUITES_PROMISE = null;
        })
        .catch(() => {
          FETCH_SUITES_PROMISE = null;
        });
    }
    return FETCH_SUITES_PROMISE;
  };
}

const FAVORITES_MODAL = 'disable_cards_favorites_modal';

export function openFavoritesModalIfNeeded(): AppThunk<void> {
  return (dispatch): void => {
    checkUserFlag(FAVORITES_MODAL)
      .then(isModalDisabled => {
        if (isModalDisabled) return;

        // Disable modal for next time.
        setUserSetting(FAVORITES_MODAL).catch(noop);

        // Open modal
        dispatch(openModal('FAVORITES_MODAL', { closeModal: () => dispatch(hideModal()) }));
      })
      .catch(noop);
  };
}
